import { useEffect, useRef } from "react";
import * as THREE from "three";

const rand = (s) => () => { s = (s * 16807) % 2147483647; return (s - 1) / 2147483646; };

function buildChain() {
  const r = rand(7);
  const pts = [];
  let pos = new THREE.Vector3();
  let dir = new THREE.Vector3(1, 0, 0);
  const plan = [["h", 14], ["l", 5], ["s", 7], ["l", 4], ["s", 7], ["l", 5], ["h", 12], ["l", 4], ["s", 6], ["l", 4], ["h", 11]];
  plan.forEach(([type, n]) => {
    const q = new THREE.Quaternion().setFromUnitVectors(new THREE.Vector3(0, 0, 1), dir.clone().normalize());
    const local = [];
    for (let i = 1; i <= n; i++) {
      if (type === "h") local.push(new THREE.Vector3(2.3 * Math.cos(i * 1.745) - 2.3, 2.3 * Math.sin(i * 1.745), 1.5 * i));
      else if (type === "s") local.push(new THREE.Vector3(i % 2 ? 0.9 : -0.9, 0, 3.3 * i));
      else local.push(new THREE.Vector3((r() - 0.5) * 4, (r() - 0.5) * 4, 2.6 * i));
    }
    local.forEach((p) => pts.push(p.applyQuaternion(q).add(pos)));
    pos = pts[pts.length - 1].clone();
    dir = new THREE.Vector3(r() - 0.5, r() - 0.5, r() - 0.5).normalize().add(pos.clone().multiplyScalar(-0.04)).normalize();
  });
  const c = new THREE.Vector3();
  pts.forEach((p) => c.add(p));
  c.divideScalar(pts.length);
  pts.forEach((p) => p.sub(c));
  return pts;
}

const PALETTE = {
  light: { fog: 0xf7f8f6, tube: 0x7fb8ae, tubeE: 0x000000, node: 0x1f7a6c, nodeE: 0x08332c, flag: 0xe08a1e, flagE: 0x5a3005, ring: 0xc46a0e, amb: 0.9, key: 0xffffff, keyI: 1.8, rim: 40 },
  dark: { fog: 0x0a1110, tube: 0x1f7a6c, tubeE: 0x0c3a33, node: 0x34c3ad, nodeE: 0x0f4a41, flag: 0xf0a23b, flagE: 0x7a4a0e, ring: 0xf0a23b, amb: 0.5, key: 0xdffaf4, keyI: 1.6, rim: 60 },
};

export const BackboneCanvas = ({ dark = false }) => {
  const ref = useRef(null);

  useEffect(() => {
    const el = ref.current;
    const P = PALETTE[dark ? "dark" : "light"];
    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    el.appendChild(renderer.domElement);
    const scene = new THREE.Scene();
    scene.fog = new THREE.Fog(P.fog, 45, 100);
    const camera = new THREE.PerspectiveCamera(40, 1, 0.1, 200);
    camera.position.set(0, 0, 70);

    const group = new THREE.Group();
    scene.add(group);
    const pts = buildChain();
    const curve = new THREE.CatmullRomCurve3(pts);
    const tube = new THREE.Mesh(
      new THREE.TubeGeometry(curve, pts.length * 6, 0.32, 8, false),
      new THREE.MeshStandardMaterial({ color: P.tube, emissive: P.tubeE, roughness: 0.5, metalness: 0.05 })
    );
    group.add(tube);

    const flagged = new Set([9, 23, 41, 62, 77]);
    const sphere = new THREE.SphereGeometry(0.62, 16, 16);
    const teal = new THREE.MeshStandardMaterial({ color: P.node, emissive: P.nodeE, roughness: 0.35 });
    const amber = new THREE.MeshStandardMaterial({ color: P.flag, emissive: P.flagE, roughness: 0.3 });
    const nodes = new THREE.InstancedMesh(sphere, teal, pts.length);
    const m = new THREE.Matrix4();
    pts.forEach((p, i) => { m.makeTranslation(p.x, p.y, p.z); nodes.setMatrixAt(i, flagged.has(i) ? m.clone().scale(new THREE.Vector3(0.001, 0.001, 0.001)) : m); });
    group.add(nodes);
    const halos = [];
    flagged.forEach((i) => {
      if (!pts[i]) return;
      const s = new THREE.Mesh(new THREE.SphereGeometry(0.95, 20, 20), amber);
      s.position.copy(pts[i]);
      const ring = new THREE.Mesh(new THREE.RingGeometry(1.6, 1.75, 40), new THREE.MeshBasicMaterial({ color: P.ring, transparent: true, opacity: 0.7, side: THREE.DoubleSide }));
      ring.position.copy(pts[i]);
      group.add(s, ring);
      halos.push(ring);
    });

    scene.add(new THREE.AmbientLight(0xffffff, P.amb));
    const key = new THREE.DirectionalLight(P.key, P.keyI);
    key.position.set(20, 30, 40);
    scene.add(key);
    const rim = new THREE.PointLight(0xf0a23b, P.rim, 120);
    rim.position.set(-30, -10, 20);
    scene.add(rim);

    const resize = () => {
      const { clientWidth: w, clientHeight: h } = el;
      renderer.setSize(w, h);
      camera.aspect = w / h;
      camera.position.z = w < 640 ? 64 : 70;
      camera.updateProjectionMatrix();
    };
    resize();
    window.addEventListener("resize", resize);

    const mouse = { x: 0, y: 0 };
    const onMove = (e) => { mouse.x = e.clientX / window.innerWidth - 0.5; mouse.y = e.clientY / window.innerHeight - 0.5; };
    window.addEventListener("pointermove", onMove);

    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    let visible = true;
    const io = new IntersectionObserver(([e]) => { visible = e.isIntersecting; });
    io.observe(el);

    let raf;
    const clock = new THREE.Clock();
    const tick = () => {
      raf = requestAnimationFrame(tick);
      if (!visible) return;
      const t = clock.getElapsedTime();
      const spin = reduced ? 0 : t * 0.12;
      group.rotation.y += (spin + mouse.x * 0.9 - group.rotation.y) * 0.05;
      group.rotation.x += (0.25 + mouse.y * 0.6 + window.scrollY * 0.0008 - group.rotation.x) * 0.05;
      halos.forEach((h, i) => { h.lookAt(camera.position); h.scale.setScalar(1 + 0.15 * Math.sin(t * 2 + i)); });
      renderer.render(scene, camera);
    };
    tick();

    return () => {
      cancelAnimationFrame(raf);
      io.disconnect();
      window.removeEventListener("resize", resize);
      window.removeEventListener("pointermove", onMove);
      renderer.dispose();
      el.removeChild(renderer.domElement);
    };
  }, [dark]);

  return <div ref={ref} data-testid="hero-backbone-canvas" className="absolute inset-0" aria-hidden="true" />;
};
